/**
 * Profile definitions for sample memory generation
 */

import { seeingMachinesProfile } from './profiles.seeing-machines';
import type { DemoMemoryPreset } from './types';

export interface Profile {
  name: string;
  minMeetingsPerDay: number;
  sessions: Array<ProfileSession>;
  meetings: Array<ProfileCalendarSession>;
  averageSwitchingFrequencySeconds: number;
}

export interface ProfileSession {
  name: string;
  /// Default: 45
  maxLengthMinutes?: number;
  entries: Array<ProfileSessionEntry>;
  predominantTool: string;
  aiNotes: {
    concise: string;
    detailed: string;
  };
}

export interface ProfileSessionEntry {
  /// If not set, use URL to build tool id
  tool?: string;
  url?: string;
  title: string;
}

export interface ProfileCalendarSession {
  title: string;
  durationMinutes: number;
  entries: Array<ProfileSessionEntry>;
  aiNotes: {
    concise: string;
    detailed: string;
  };
}

/**
 * Developer profile with realistic tasks and projects
 */
export const developerProfile: Profile = {
  name: 'Developer',
  averageSwitchingFrequencySeconds: 300, // Switch context every ~5 minutes on average
  minMeetingsPerDay: 1,
  meetings: [
    {
      title: 'Daily Standup',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Daily Standup Notes - June 17' },
        { tool: 'linear', title: 'Sprint Board - Current Tasks' },
        { tool: 'slack', title: 'Thread: Standup Follow-ups' },
      ],
      aiNotes: {
        concise: 'Daily team sync discussing current sprint tasks.',
        detailed:
          'Participating in daily standup meeting, reviewing current tasks on the sprint board, taking notes, and following up on action items through Slack threads.',
      },
    },
    {
      title: 'Frontend Team Weekly',
      durationMinutes: 60,
      entries: [
        { tool: 'notion', title: 'Frontend Team Meeting Notes - Q3W2' },
        { tool: 'figma', title: 'UI Component Review - June 2024' },
        { tool: 'linear', title: 'Frontend Team Tasks Q3' },
        { tool: 'visual_studio_code', title: 'components/README.md' },
      ],
      aiNotes: {
        concise: 'Weekly frontend team sync reviewing UI components and tasks.',
        detailed:
          'Participating in weekly frontend team meeting, taking notes, reviewing UI components in Figma, discussing Q3 tasks in Linear, and examining component documentation.',
      },
    },
    {
      title: 'TT-4312: Auth System Planning',
      durationMinutes: 60,
      entries: [
        { tool: 'notion', title: 'Auth System Design Document v2' },
        { tool: 'visual_studio_code', title: 'auth/architecture.md' },
        { tool: 'excalidraw', title: 'JWT Refresh Flow Diagram' },
        { tool: 'linear', title: 'TT-4312: Implement JWT refresh token flow' },
        { tool: 'slack', title: 'Thread: Auth System Questions' },
      ],
      aiNotes: {
        concise: 'Planning JWT refresh token authentication system implementation.',
        detailed:
          'Designing authentication system architecture, creating JWT refresh flow diagrams, documenting technical specifications, planning implementation tasks, and discussing system requirements with team members.',
      },
    },
    {
      title: '1:1 with Rachel (Engineering Manager)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Career Development Plan - Q3 2024' },
        { tool: 'notion', title: '1:1 Notes and Action Items - June 17' },
        { tool: 'slack', title: 'Rachel (DM)' },
      ],
      aiNotes: {
        concise: 'Career development discussion with engineering manager.',
        detailed:
          'Meeting with engineering manager to discuss Q3 career development plans, documenting action items and follow-ups, and exchanging messages about professional growth goals.',
      },
    },
    {
      title: 'TT-4280: API Integration Review',
      durationMinutes: 45,
      entries: [
        { tool: 'notion', title: 'API Integration Documentation' },
        { tool: 'visual_studio_code', title: 'services/apiClient.ts' },
        { tool: 'visual_studio_code', title: 'types/api.d.ts' },
        { tool: 'linear', title: 'TT-4280: Update API client for v2 endpoints' },
        { tool: 'postman', title: 'API Endpoint Tests' },
      ],
      aiNotes: {
        concise: 'Reviewing API client updates for v2 endpoints.',
        detailed:
          'Meeting to review API integration development, examining API client implementation code, updating type definitions, tracking progress on Linear tasks, and testing endpoints using Postman.',
      },
    },
    {
      title: 'Q3 Sprint Planning',
      durationMinutes: 90,
      entries: [
        { tool: 'notion', title: 'Sprint Planning Document - Q3' },
        { tool: 'linear', title: 'Sprint Backlog - Q3' },
        { tool: 'figma', title: 'Q3 Roadmap Visual' },
        { tool: 'slack', title: 'sprint-planning' },
      ],
      aiNotes: {
        concise: 'Planning and prioritizing Q3 sprint tasks and deliverables.',
        detailed:
          'Participating in Q3 sprint planning session, reviewing backlog items in Linear, documenting planning decisions in Notion, referring to roadmap visuals, and coordinating with team via Slack.',
      },
    },
    {
      title: 'Sprint Review & Demo',
      durationMinutes: 60,
      entries: [
        { tool: 'visual_studio_code', title: 'demo/AuthDemo.tsx' },
        { tool: 'notion', title: 'Sprint Review Notes' },
        { tool: 'linear', title: 'Completed Tasks - Sprint 24' },
        { tool: 'figma', title: 'Demo Slides - Sprint 24' },
      ],
      aiNotes: {
        concise: 'Demonstrating authentication features completed in sprint.',
        detailed:
          'Presenting completed sprint work to team, demonstrating new authentication features, reviewing sprint accomplishments, taking notes on feedback, and discussing completed Linear tasks.',
      },
    },
    {
      title: 'TT-3890: Dashboard Performance Review',
      durationMinutes: 45,
      entries: [
        { tool: 'visual_studio_code', title: 'components/Dashboard/PerformanceChart.tsx' },
        { tool: 'notion', title: 'Performance Optimization Notes' },
        { tool: 'linear', title: 'TT-3890: Fix dashboard rendering performance' },
        { tool: 'slack', title: 'Thread: Performance Improvement Ideas' },
      ],
      aiNotes: {
        concise: 'Analyzing dashboard performance issues and optimization strategies.',
        detailed:
          'Reviewing dashboard component performance bottlenecks, discussing optimization techniques, documenting findings in performance notes, and gathering improvement suggestions through Slack discussion.',
      },
    },
    {
      title: 'Code Review Session',
      durationMinutes: 60,
      entries: [
        { tool: 'github', title: 'PR #423: Implement JWT refresh token flow' },
        { tool: 'visual_studio_code', title: 'auth/UserAuthentication.ts' },
        { tool: 'slack', title: 'code-reviews' },
        { tool: 'notion', title: 'Code Review Guidelines' },
      ],
      aiNotes: {
        concise: 'Reviewing JWT refresh token implementation PR with team.',
        detailed:
          'Conducting code review of JWT authentication implementation, examining authentication code in VS Code, following team code review guidelines, and discussing changes through Slack channels.',
      },
    },
    {
      title: 'Engineering All-Hands',
      durationMinutes: 60,
      entries: [],
      aiNotes: {
        concise: 'Attending engineering department company-wide meeting.',
        detailed:
          'Participating in engineering department all-hands meeting, receiving updates on company initiatives, department goals, and ongoing projects.',
      },
    },
  ],
  sessions: [
    {
      name: 'Feature Development - Authentication System',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'auth/UserAuthentication.ts' },
        { tool: 'visual_studio_code', title: 'auth/AuthContext.tsx' },
        { tool: 'visual_studio_code', title: 'auth/useAuth.ts' },
        { tool: 'visual_studio_code', title: 'components/LoginForm.tsx' },
        { tool: 'visual_studio_code', title: 'services/authService.ts' },
        { tool: 'visual_studio_code', title: 'types/auth.d.ts' },
        { tool: 'iTerm2', title: 'npm test -- --watch auth' },
        { tool: 'iTerm2', title: 'git status' },
        { tool: 'github', title: 'PR #423: Implement JWT refresh token flow' },
        { tool: 'linear', title: 'TT-4312: Implement JWT refresh token flow' },
        { url: 'https://github.com', title: 'GitHub - JWT authentication best practices' },
        { url: 'https://jwt.io', title: 'JWT.io - Debugger' },
      ],
      aiNotes: {
        concise: 'Developing authentication system with JWT refresh token flow.',
        detailed:
          'Working on the authentication system implementation in VSCode, focusing on JWT refresh token flow across multiple files including UserAuthentication.ts, AuthContext.tsx, and LoginForm.tsx. Running tests, checking git status, and referencing JWT documentation.',
      },
    },
    {
      name: 'Bug Fix - Dashboard Performance',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/Dashboard/PerformanceChart.tsx' },
        { tool: 'visual_studio_code', title: 'utils/performanceMetrics.ts' },
        { tool: 'visual_studio_code', title: 'hooks/useDashboardData.ts' },
        { tool: 'visual_studio_code', title: 'services/metricsService.ts' },
        { tool: 'iTerm2', title: 'npm run profile:dashboard' },
        { tool: 'iTerm2', title: 'git diff components/Dashboard/' },
        { tool: 'github', title: 'Issue #271: Dashboard performance degradation' },
        { tool: 'linear', title: 'TT-3890: Fix dashboard rendering performance' },
        { url: 'https://web.dev', title: 'Web.dev - Core Web Vitals optimization' },
        { url: 'https://reactjs.org', title: 'React docs - Profiler API' },
        { url: 'https://youtube.com', title: 'YouTube - React Performance with memo and useMemo' },
      ],
      aiNotes: {
        concise: 'Fixing dashboard performance issues and optimizing rendering.',
        detailed:
          'Debugging and fixing performance issues in the dashboard components, focusing on PerformanceChart.tsx and related files. Running performance profiling, analyzing code differences, and researching React optimization techniques with memo and useMemo.',
      },
    },
    {
      name: 'Code Review',
      predominantTool: 'github',
      entries: [
        { tool: 'github', title: 'PR #419: Migrate API endpoints to v2' },
        { tool: 'github', title: 'PR #422: Refactor notification system' },
        { tool: 'github', title: 'PR #408: Add team management features' },
        { tool: 'visual_studio_code', title: 'api/endpoints.ts' },
        { tool: 'visual_studio_code', title: 'services/NotificationService.ts' },
        { tool: 'visual_studio_code', title: 'components/TeamManagement/TeamForm.tsx' },
        { tool: 'iTerm2', title: 'git checkout feature/notification-refactor' },
        { tool: 'iTerm2', title: 'npm test services/notifications' },
        { url: 'https://github.com', title: 'GitHub - Pull request review guidelines' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - Efficient code review practices',
        },
      ],
      aiNotes: {
        concise:
          'Reviewing pull requests for API migration, notification system, and team features.',
        detailed:
          'Performing code reviews on GitHub for multiple PRs including API endpoint migration to v2, notification system refactoring, and team management features. Checking out branches, examining code files, and running tests on the notification service.',
      },
    },
    {
      name: 'Project Planning',
      predominantTool: 'linear',
      entries: [
        { tool: 'linear', title: 'Q3 Roadmap: Analytics Dashboard' },
        { tool: 'linear', title: 'TT-4350: Implement user activity tracking' },
        { tool: 'linear', title: 'TT-4427: Improve error handling in API client' },
        { tool: 'notion', title: 'Architecture: Data Flow Diagrams' },
        { tool: 'notion', title: 'Tech Spec: Analytics Service' },
        { tool: 'slack', title: 'tech-planning' },
        { tool: 'slack', title: 'Sarah (DM)' },
        { url: 'https://miro.com', title: 'Miro - Project roadmap visualization' },
        { url: 'https://productplan.com', title: 'ProductPlan - Q3 product roadmap' },
        { url: 'https://atlassian.com', title: 'Atlassian - Agile project management guide' },
      ],
      aiNotes: {
        concise: 'Planning analytics dashboard features and API improvements.',
        detailed:
          'Planning Q3 roadmap for analytics dashboard, user activity tracking, and API error handling. Creating data flow diagrams, tech specs, and collaborating with team through Slack and project visualization tools.',
      },
    },
    {
      name: 'Team Communication',
      predominantTool: 'slack',
      maxLengthMinutes: 25,
      entries: [
        { tool: 'slack', title: 'dev-team' },
        { tool: 'slack', title: 'frontend' },
        { tool: 'slack', title: 'Michael (DM)' },
        { tool: 'slack', title: 'Jessica (DM)' },
        { tool: 'gmail', title: 'Email: Weekly Team Update' },
        { tool: 'notion', title: 'Team Communication Guidelines' },
        { url: 'https://calendar.google.com', title: 'Google Calendar - Team availability' },
        { url: 'https://slack.com', title: 'Slack - Workspace settings' },
      ],
      aiNotes: {
        concise: 'Coordinating with team members via chat and email.',
        detailed:
          'Communicating with team members through Slack channels and direct messages, reviewing weekly updates via email, and checking team availability on Google Calendar.',
      },
    },
    {
      name: 'API Integration',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'services/apiClient.ts' },
        { tool: 'visual_studio_code', title: 'hooks/useApi.ts' },
        { tool: 'visual_studio_code', title: 'utils/requestUtils.ts' },
        { tool: 'visual_studio_code', title: 'types/api.d.ts' },
        { tool: 'iTerm2', title: 'curl -X GET https://api.example.com/v2/users' },
        { tool: 'iTerm2', title: 'npm test services/apiClient' },
        { tool: 'linear', title: 'TT-4280: Update API client for v2 endpoints' },
        { tool: 'postman', title: 'API Testing - V2 Endpoints' },
        { url: 'https://npmjs.com', title: 'npm - react-query package' },
        { url: 'https://tanstack.com', title: 'TanStack Query Documentation' },
        { url: 'https://swagger.io', title: 'Swagger - API Schema Documentation' },
        { url: 'https://developer.mozilla.org', title: 'MDN - Fetch API Reference' },
      ],
      aiNotes: {
        concise: 'Implementing API client updates for v2 endpoints.',
        detailed:
          'Working on API client integration in VSCode, developing apiClient.ts, useApi hooks, and type definitions. Testing API endpoints with curl and Postman, referencing TanStack Query documentation and Swagger API schemas.',
      },
    },
    {
      name: 'TypeScript Learning',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'types/advanced-types.ts' },
        { tool: 'visual_studio_code', title: 'utils/type-helpers.ts' },
        { url: 'https://typescriptlang.org', title: 'TypeScript - Advanced Types' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - TypeScript generics question',
        },
      ],
      aiNotes: {
        concise: 'Studying advanced TypeScript type concepts.',
        detailed:
          'Learning advanced TypeScript features, focusing on complex type definitions and helper functions. Researching TypeScript documentation and Stack Overflow posts about generics implementation.',
      },
    },
    {
      name: 'React Component Optimization',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/DataTable.tsx' },
        { tool: 'visual_studio_code', title: 'hooks/useOptimizedQuery.ts' },
        { tool: 'iTerm2', title: 'npm run test:performance' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - useEffect dependency warnings',
        },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - React performance optimization techniques',
        },
      ],
      aiNotes: {
        concise: 'Optimizing DataTable component performance.',
        detailed:
          'Improving performance of the DataTable component and creating optimized query hooks. Running performance tests and researching React optimization techniques for useEffect dependencies.',
      },
    },
    {
      name: 'Web API Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://developer.mozilla.org', title: 'MDN - Intersection Observer API' },
        { url: 'https://developer.mozilla.org', title: 'MDN - Service Workers API' },
        { url: 'https://developer.mozilla.org', title: 'MDN - Cache Storage API' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - Service worker caching strategies',
        },
        { tool: 'visual_studio_code', title: 'utils/intersectionObserver.ts' },
      ],
      aiNotes: {
        concise: 'Researching modern web API implementations.',
        detailed:
          'Studying modern web APIs including Intersection Observer, Service Workers, and Cache Storage. Implementing an intersection observer utility and researching service worker caching strategies.',
      },
    },
    {
      name: 'Authentication Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://google.com', title: 'Google search: NextJS API routes authentication' },
        { url: 'https://auth0.com', title: 'Auth0 - Modern Authentication Patterns' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - NextJS API route auth strategies',
        },
        { url: 'https://jwt.io', title: 'JWT.io - Token structure analysis' },
        { tool: 'notion', title: 'Authentication Research Notes' },
      ],
      aiNotes: {
        concise: 'Researching authentication patterns for NextJS API routes.',
        detailed:
          'Researching authentication methods for NextJS API routes, studying modern auth patterns from Auth0, analyzing JWT token structures, and documenting findings in authentication research notes.',
      },
    },
    {
      name: 'Diagramming',
      predominantTool: 'excalidraw',
      entries: [
        { tool: 'excalidraw', title: 'Data Flow Diagram - New Feature' },
        { tool: 'excalidraw', title: 'Component Hierarchy - Dashboard' },
        { url: 'https://excalidraw.com', title: 'Excalidraw - Authentication flow diagram' },
        { url: 'https://google.com', title: 'Google search: System design diagram examples' },
        { tool: 'slack', title: 'Thread: Architecture Discussion' },
      ],
      aiNotes: {
        concise: 'Creating technical diagrams for feature architecture.',
        detailed:
          'Creating data flow and component hierarchy diagrams using Excalidraw, designing authentication flow diagrams, and discussing architecture with team members through Slack.',
      },
    },
    {
      name: 'CI/CD Pipeline Configuration',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: '.github/workflows/main.yml' },
        { tool: 'visual_studio_code', title: 'scripts/deploy.sh' },
        { tool: 'visual_studio_code', title: '.github/workflows/test.yml' },
        { tool: 'github', title: 'Repository Actions Configuration' },
        { tool: 'github', title: 'CI/CD Pipeline Runs' },
        { tool: 'iTerm2', title: 'npm run test:ci' },
        { tool: 'slack', title: 'ci-cd-alerts' },
        { url: 'https://docs.github.com', title: 'GitHub Actions Documentation' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - GitHub Actions workflow syntax',
        },
        { url: 'https://circleci.com', title: 'CircleCI - Pipeline configuration reference' },
      ],
      aiNotes: {
        concise: 'Setting up GitHub Actions workflows for CI/CD.',
        detailed:
          'Configuring GitHub Actions workflows for continuous integration and deployment, writing deployment scripts, running CI tests, and monitoring alerts through Slack. Referencing GitHub Actions and CircleCI documentation.',
      },
    },
    {
      name: 'Database Schema Design',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'prisma/schema.prisma' },
        { tool: 'visual_studio_code', title: 'server/models/database.ts' },
        {
          tool: 'visual_studio_code',
          title: 'server/migrations/202406121523_add_user_preferences.ts',
        },
        { tool: 'iTerm2', title: 'npx prisma migrate dev' },
        { tool: 'iTerm2', title: 'npx prisma studio' },
        { tool: 'linear', title: 'TT-4251: Update user preferences schema' },
        { url: 'https://prisma.io/docs', title: 'Prisma Documentation - Schema definition' },
        { url: 'https://dbdiagram.io', title: 'DB Diagram - User system schema visualization' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - Database relationship modeling',
        },
      ],
      aiNotes: {
        concise: 'Developing database schema for user preferences.',
        detailed:
          'Creating and updating Prisma schema for database models, implementing user preferences migration, running Prisma commands for migrations and visualization, and researching database relationship modeling.',
      },
    },
    {
      name: 'Performance Profiling',
      predominantTool: 'google_chrome',
      entries: [
        { tool: 'google_chrome', title: 'Chrome DevTools - Performance tab' },
        { tool: 'google_chrome', title: 'Chrome DevTools - Memory tab' },
        { tool: 'visual_studio_code', title: 'components/DataTable/TableRow.tsx' },
        { tool: 'visual_studio_code', title: 'utils/memoization.ts' },
        { tool: 'iTerm2', title: 'npm run analyze-bundle' },
        { tool: 'iTerm2', title: 'node --inspect server.js' },
        { tool: 'linear', title: 'TT-4118: Optimize table rendering performance' },
        { url: 'https://web.dev/performance', title: 'Web.dev - Performance optimization guide' },
        { url: 'https://reactjs.org/docs/profiler.html', title: 'React Profiler Documentation' },
        { url: 'https://developer.chrome.com', title: 'Chrome DevTools Performance Analysis' },
      ],
      aiNotes: {
        concise: 'Profiling and optimizing table rendering performance.',
        detailed:
          'Using Chrome DevTools to analyze performance and memory usage, optimizing DataTable components, running bundle analysis, and implementing memoization techniques for improved rendering efficiency.',
      },
    },
    {
      name: 'Microservices Architecture Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://microservices.io', title: 'Microservices architecture patterns' },
        { url: 'https://martinfowler.com', title: 'Martin Fowler - Microservices guide' },
        { url: 'https://kubernetes.io', title: 'Kubernetes documentation' },
        { tool: 'visual_studio_code', title: 'architecture/microservices.md' },
        { tool: 'notion', title: 'Microservices Architecture Notes' },
      ],
      aiNotes: {
        concise: 'Researching microservices patterns and best practices.',
        detailed:
          'Studying microservices architecture patterns and implementation guides, reviewing Kubernetes documentation for container orchestration, and documenting findings in architecture notes.',
      },
    },
    {
      name: 'Team Mentoring Session',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'junior-tasks/auth-improvements.md' },
        { tool: 'visual_studio_code', title: 'demos/authentication-flow.tsx' },
        { tool: 'slack', title: 'mentoring-program' },
        { tool: 'notion', title: 'Junior Developer Onboarding Guide' },
      ],
      aiNotes: {
        concise: 'Mentoring junior developers on authentication flows.',
        detailed:
          'Conducting mentoring session for junior developers, focusing on authentication improvements, creating demonstration components, and reviewing onboarding documentation for new team members.',
      },
    },
    {
      name: 'Frontend Refactoring',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/UserProfile/UserProfile.tsx' },
        { tool: 'visual_studio_code', title: 'components/UserProfile/UserAvatar.tsx' },
        { tool: 'visual_studio_code', title: 'components/UserProfile/UserSettings.tsx' },
        { tool: 'visual_studio_code', title: 'styles/components/profile.scss' },
        { tool: 'iTerm2', title: 'npm run test:watch -- UserProfile' },
        { tool: 'linear', title: 'TT-4387: Refactor UserProfile component' },
      ],
      aiNotes: {
        concise: 'Refactoring UserProfile component structure.',
        detailed:
          'Restructuring UserProfile component architecture, separating concerns into subcomponents, updating styles, and running component tests to validate changes during the refactoring process.',
      },
    },
    {
      name: 'DevOps Improvement',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'docker-compose.yml' },
        { tool: 'visual_studio_code', title: 'Dockerfile.dev' },
        { tool: 'visual_studio_code', title: 'scripts/ci-build.sh' },
        { tool: 'iTerm2', title: 'docker-compose up -d' },
        { tool: 'iTerm2', title: 'docker logs -f app' },
        { tool: 'linear', title: 'TT-4401: Improve local development environment' },
      ],
      aiNotes: {
        concise: 'Enhancing local development environment with Docker.',
        detailed:
          'Improving local development environment by configuring Docker containers, writing deployment scripts, running and monitoring containers, and tracking progress through Linear task management.',
      },
    },
    {
      name: 'A/B Testing Implementation',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'utils/abTesting.ts' },
        { tool: 'visual_studio_code', title: 'components/Experiment/ABTest.tsx' },
        { tool: 'visual_studio_code', title: 'components/Experiment/Variant.tsx' },
        { tool: 'visual_studio_code', title: 'hooks/useExperiment.ts' },
        { tool: 'notion', title: 'A/B Testing Strategy - Q3 2024' },
        { tool: 'linear', title: 'TT-4325: Implement A/B testing framework' },
      ],
      aiNotes: {
        concise: 'Building A/B testing framework for experiments.',
        detailed:
          'Implementing A/B testing framework with experiment components, variants handling, custom hooks for experiment management, and documenting the testing strategy for Q3 implementation.',
      },
    },
    {
      name: 'State Management Improvement',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'store/index.ts' },
        { tool: 'visual_studio_code', title: 'store/user/actions.ts' },
        { tool: 'visual_studio_code', title: 'store/user/reducers.ts' },
        { tool: 'visual_studio_code', title: 'store/user/selectors.ts' },
        { tool: 'visual_studio_code', title: 'hooks/useAppSelector.ts' },
        { tool: 'linear', title: 'TT-4290: Improve state management architecture' },
      ],
      aiNotes: {
        concise: 'Enhancing Redux store architecture and selectors.',
        detailed:
          'Improving state management architecture by refactoring Redux store organization, updating user actions, reducers, and selectors, and creating optimized hooks for state access.',
      },
    },
    {
      name: 'API Error Handling',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'utils/errorHandling.ts' },
        { tool: 'visual_studio_code', title: 'components/ErrorBoundary.tsx' },
        { tool: 'visual_studio_code', title: 'components/ErrorMessage.tsx' },
        { tool: 'visual_studio_code', title: 'hooks/useErrorHandler.ts' },
        { tool: 'iTerm2', title: 'npm test -- ErrorBoundary' },
        { tool: 'linear', title: 'TT-4315: Improve API error handling' },
      ],
      aiNotes: {
        concise: 'Implementing robust error handling for API operations.',
        detailed:
          'Developing comprehensive error handling utilities for API operations, creating ErrorBoundary and ErrorMessage components, implementing custom error handling hooks, and testing error boundary implementation.',
      },
    },
    {
      name: 'Accessibility Improvement',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/common/Button.tsx' },
        { tool: 'visual_studio_code', title: 'components/common/FormField.tsx' },
        { tool: 'visual_studio_code', title: 'utils/accessibility.ts' },
        { tool: 'visual_studio_code', title: 'styles/accessibility.scss' },
        { tool: 'google_chrome', title: 'Chrome DevTools - Accessibility audit' },
        { tool: 'linear', title: 'TT-4399: Improve WCAG compliance' },
      ],
      aiNotes: {
        concise: 'Enhancing WCAG compliance in UI components.',
        detailed:
          'Improving accessibility of common UI components like buttons and form fields, writing accessibility utility functions, conducting accessibility audits in Chrome DevTools, and tracking WCAG compliance tasks.',
      },
    },
    {
      name: 'Unit Testing',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'tests/components/UserProfile.test.tsx' },
        { tool: 'visual_studio_code', title: 'tests/utils/auth.test.ts' },
        { tool: 'visual_studio_code', title: 'tests/hooks/useAuth.test.ts' },
        { tool: 'iTerm2', title: 'npm test -- --coverage' },
        { tool: 'github', title: 'PR #429: Increase test coverage' },
        { tool: 'linear', title: 'TT-4330: Improve test coverage' },
      ],
      aiNotes: {
        concise: 'Increasing test coverage for components and utilities.',
        detailed:
          'Writing unit tests for UserProfile components, authentication utilities, and custom hooks. Running test coverage reports and submitting pull requests to improve overall test coverage quality.',
      },
    },
    {
      name: 'Performance Optimization',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'webpack.config.js' },
        { tool: 'visual_studio_code', title: 'utils/performance.ts' },
        { tool: 'visual_studio_code', title: 'components/LazyImage.tsx' },
        { tool: 'iTerm2', title: 'npm run analyze-bundle' },
        { tool: 'google_chrome', title: 'Chrome DevTools - Performance tab' },
        { tool: 'linear', title: 'TT-4318: Optimize bundle size' },
      ],
      aiNotes: {
        concise: 'Optimizing application bundle size and loading performance.',
        detailed:
          'Configuring webpack for optimized builds, implementing performance utilities, creating lazy-loaded image components, analyzing bundle size, and optimizing application load performance.',
      },
    },
  ],
};

/**
 * Designer profile with specific design projects
 */
export const designerProfile: Profile = {
  name: 'Designer',
  minMeetingsPerDay: 1,
  averageSwitchingFrequencySeconds: 600, // Switch context every ~10 minutes on average
  meetings: [
    {
      title: 'Design Team Weekly Sync',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Weekly Design Review Board - Q3W2' },
        { tool: 'notion', title: 'Design Team Weekly Notes - June 2024' },
        { tool: 'linear', title: 'Design Team Tasks - Current Sprint' },
      ],
      aiNotes: {
        concise: 'Reviewing design work and planning sprint tasks.',
        detailed:
          'Participating in weekly design team sync meeting, reviewing design work in Figma, documenting discussion points, and organizing current sprint tasks in Linear.',
      },
    },
    {
      title: 'TT-4024: Mobile App Design Review',
      durationMinutes: 90,
      entries: [
        { tool: 'figma', title: 'Mobile app - Dashboard Redesign v3' },
        { tool: 'figma', title: 'Mobile app - Navigation Flow' },
        { tool: 'notion', title: 'Mobile App Review Notes - June 17' },
        { tool: 'linear', title: 'TT-4024: Mobile app navigation redesign' },
      ],
      aiNotes: {
        concise: 'Evaluating mobile app dashboard and navigation designs.',
        detailed:
          'Reviewing mobile app dashboard redesign and navigation flow in Figma, taking detailed notes on feedback, and updating the navigation redesign task in Linear.',
      },
    },
    {
      title: 'User Testing Session: Dashboard v3',
      durationMinutes: 120,
      entries: [
        { tool: 'figma', title: 'Analytics Dashboard - User Testing Prototype' },
        { tool: 'notion', title: 'User Testing Script - Dashboard v3' },
        { tool: 'notion', title: 'User Testing Live Notes' },
      ],
      aiNotes: {
        concise: 'Conducting user testing for dashboard prototype.',
        detailed:
          'Facilitating user testing session for the analytics dashboard prototype, following test script, and documenting user feedback and observations in real-time notes.',
      },
    },
    {
      title: '1:1 with Alex (Design Director)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Career Growth Plan - 2024' },
        { tool: 'figma', title: 'Portfolio Update - June 2024' },
        { tool: 'slack', title: 'Alex (DM)' },
      ],
      aiNotes: {
        concise: 'Discussing career growth and portfolio development.',
        detailed:
          'Meeting with Design Director to review career growth plan, presenting updated portfolio work in Figma, and exchanging feedback via direct messages.',
      },
    },
    {
      title: 'TT-4127: Dashboard Redesign Planning',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Analytics Dashboard - Data Visualization' },
        { tool: 'notion', title: 'Dashboard Redesign Project Brief' },
        { tool: 'linear', title: 'TT-4127: Improve dashboard data visualization' },
      ],
      aiNotes: {
        concise: 'Planning dashboard data visualization improvements.',
        detailed:
          'Planning analytics dashboard redesign with focus on data visualization, reviewing project brief documentation, and updating task requirements in Linear.',
      },
    },
    {
      title: 'TT-3928: Q3 Brand Refresh Presentation',
      durationMinutes: 45,
      entries: [
        { tool: 'figma', title: 'Brand refresh 2025 - Color System' },
        { tool: 'figma', title: 'Brand refresh 2025 - Presentation Deck' },
        { tool: 'notion', title: 'Brand Refresh Implementation Plan' },
      ],
      aiNotes: {
        concise: 'Presenting brand refresh concept and implementation plan.',
        detailed:
          'Presenting brand refresh concept featuring updated color system in Figma, walking through presentation deck, and discussing the implementation plan timeline with stakeholders.',
      },
    },
    {
      title: 'WCAG 2.2 Accessibility Workshop',
      durationMinutes: 90,
      entries: [
        { tool: 'figma', title: 'Accessibility Audit - Current Components' },
        { tool: 'notion', title: 'WCAG 2.2 Guidelines & Requirements' },
        { tool: 'linear', title: 'Accessibility Improvement Tasks' },
      ],
      aiNotes: {
        concise: 'Learning and applying WCAG accessibility guidelines to designs.',
        detailed:
          'Participating in accessibility workshop focused on WCAG 2.2 compliance, conducting audit of current design components, reviewing guidelines, and planning improvement tasks.',
      },
    },
    {
      title: 'Design System Working Group',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Design System - Component Audit' },
        { tool: 'notion', title: 'Design System: Component Updates Q3' },
        { tool: 'linear', title: 'TT-4009: Update form controls in design system' },
      ],
      aiNotes: {
        concise: 'Collaborating on design system component updates.',
        detailed:
          'Working with design system group to audit components, planning Q3 component updates, and prioritizing form control improvements in the design system.',
      },
    },
    {
      title: 'TT-4098: User Research Findings Review',
      durationMinutes: 45,
      entries: [
        { tool: 'notion', title: 'User Research Synthesis: Dashboard Features' },
        { tool: 'figma', title: 'User Journey Maps - Enterprise Plan' },
        { tool: 'linear', title: 'TT-4098: Incorporate user research findings' },
      ],
      aiNotes: {
        concise: 'Analyzing user research data and planning design improvements.',
        detailed:
          'Reviewing synthesized user research findings for dashboard features, examining user journey maps for enterprise customers, and planning implementation of insights into design work.',
      },
    },
    {
      title: 'Design & Engineering Pairing Session',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Component Implementation Details' },
        { tool: 'github', title: 'UI Component Repository' },
        { tool: 'slack', title: 'Rachel (DM)' },
      ],
      aiNotes: {
        concise: 'Collaborating with engineering on component implementation.',
        detailed:
          'Pairing with engineering team to review component implementation details in Figma, examining UI component code repository, and communicating directly with the engineering lead.',
      },
    },
  ],
  sessions: [
    {
      name: 'Mobile App Redesign',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Mobile app - Component Library' },
        { tool: 'figma', title: 'Mobile app - Dashboard Redesign v3' },
        { tool: 'figma', title: 'Mobile app - Navigation Flow' },
        { tool: 'figma', title: 'Mobile app - Onboarding Screens' },
        { tool: 'figma', title: 'Mobile app - Dark Mode Adaptation' },
        { tool: 'figma', title: 'Mobile app - User Testing Prototypes' },
        { tool: 'figma', title: 'Mobile app - Design System Implementation' },
        { tool: 'linear', title: 'TT-4024: Mobile app navigation redesign' },
        { tool: 'slack', title: 'mobile-app-redesign' },
        { tool: 'loom', title: 'Mobile Navigation Flow Demo' },
        { url: 'https://behance.net', title: 'Behance - Mobile app navigation patterns' },
        { url: 'https://google.com', title: 'Google search: Mobile app onboarding best practices' },
        { url: 'https://mobbin.com', title: 'Mobbin - Mobile UI design patterns' },
        { url: 'https://apple.com/design', title: 'Apple Human Interface Guidelines' },
        { url: 'https://material.io/design', title: 'Google Material Design - Mobile' },
      ],
      aiNotes: {
        concise: 'Designing comprehensive mobile app interface and user flows.',
        detailed:
          'Creating mobile app designs in Figma including component library, dashboard redesign, navigation flows, onboarding screens, dark mode, and interactive prototypes. Referencing design guidelines and researching navigation patterns.',
      },
    },
    {
      name: 'Brand Refresh',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Brand 2024 - Color System' },
        { tool: 'figma', title: 'Brand 2024 - Typography' },
        { tool: 'figma', title: 'Brand 2024 - Logo Variations' },
        { tool: 'figma', title: 'Brand 2024 - Marketing Assets' },
        { tool: 'figma', title: 'Brand 2024 - Social Media Templates' },
        { tool: 'notion', title: 'Brand Guidelines Documentation' },
        { tool: 'linear', title: 'TT-3928: Brand refresh for Q3 marketing campaign' },
        { url: 'https://coolors.co', title: 'Coolors - Brand palette explorations' },
        { url: 'https://fonts.google.com', title: 'Google Fonts - Sans serif options for UI' },
        { url: 'https://unsplash.com', title: 'Unsplash - Team photography for marketing' },
      ],
      aiNotes: {
        concise: 'Updating brand identity with new colors, typography and assets.',
        detailed:
          'Developing comprehensive brand refresh design system in Figma, including color palette, typography, logo variations, marketing assets, and social media templates. Documenting brand guidelines and researching font options.',
      },
    },
    {
      name: 'Dashboard UI Improvements',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Analytics Dashboard - Data Visualization' },
        { tool: 'figma', title: 'Analytics Dashboard - User Flow' },
        { tool: 'figma', title: 'Analytics Dashboard - Responsive Layouts' },
        { tool: 'figma', title: 'Analytics Dashboard - Interactive Prototype' },
        { tool: 'notion', title: 'User Research: Dashboard Usability Findings' },
        { tool: 'linear', title: 'TT-4127: Improve dashboard data visualization' },
        { tool: 'loom', title: 'Dashboard UI review for dev team' },
        { url: 'https://dribbble.com', title: 'Dribbble - Analytics dashboard inspiration' },
        { url: 'https://nngroup.com', title: 'Nielsen Norman Group - Dashboard design guidelines' },
      ],
      aiNotes: {
        concise: 'Enhancing analytics dashboard with improved visualizations and flows.',
        detailed:
          'Redesigning analytics dashboard with focus on data visualization, user flows, responsive layouts, and interactive prototypes. Incorporating usability research findings and creating video review for development team.',
      },
    },
    {
      name: 'Design System Maintenance',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Design System - Component Audit' },
        { tool: 'figma', title: 'Design System - Form Controls Update' },
        { tool: 'figma', title: 'Design System - Documentation' },
        { tool: 'figma', title: 'Design System - Accessibility Improvements' },
        { tool: 'notion', title: 'Design System: Component Usage Guidelines' },
        { tool: 'linear', title: 'TT-4009: Update form controls in design system' },
        { url: 'https://medium.com', title: 'Medium - Design systems at scale' },
        { url: 'https://material.io', title: 'Material Design - Form components reference' },
        {
          url: 'https://contrast-ratio.com',
          title: 'Contrast Ratio Checker - Accessibility testing',
        },
      ],
      aiNotes: {
        concise: 'Maintaining and improving the design system components.',
        detailed:
          'Auditing design system components, updating form controls, improving documentation, and enhancing accessibility. Researching design systems at scale and testing color contrast for accessibility compliance.',
      },
    },
    {
      name: 'Team Communication',
      predominantTool: 'slack',
      entries: [
        { tool: 'slack', title: 'design-team' },
        { tool: 'slack', title: 'product' },
        { tool: 'slack', title: 'Alex (DM)' },
        { tool: 'slack', title: 'Rachel (DM)' },
      ],
      aiNotes: {
        concise: 'Coordinating with design and product teams.',
        detailed:
          'Communicating with design team members and product team through Slack channels and direct messages with team leads.',
      },
    },
    {
      name: 'UI Animation Research',
      predominantTool: 'google_chrome',
      entries: [
        {
          url: 'https://smashingmagazine.com',
          title: 'Smashing Magazine - UI animation principles',
        },
        { url: 'https://youtube.com', title: 'YouTube - Advanced Figma prototyping techniques' },
        { url: 'https://dribbble.com', title: 'Dribbble - Animation showcase' },
        { tool: 'figma', title: 'Mobile - Animation Prototypes' },
        { tool: 'notion', title: 'Animation Principles: Research Notes' },
      ],
      aiNotes: {
        concise: 'Researching animation principles and techniques for interfaces.',
        detailed:
          'Studying UI animation principles and advanced Figma prototyping techniques, creating animation prototypes, and documenting research findings on animation principles.',
      },
    },
    {
      name: 'User Research',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'User Interview Notes: Mobile Usability' },
        { tool: 'notion', title: 'Research Synthesis: Dashboard Features' },
        { tool: 'notion', title: 'User Persona Updates: Enterprise Users' },
        { tool: 'figma', title: 'User Journey Maps - Enterprise Plan' },
        { tool: 'loom', title: 'User Research Summary for stakeholders' },
        { tool: 'linear', title: 'TT-4098: Incorporate user research findings' },
      ],
      aiNotes: {
        concise: 'Analyzing user interview data and creating journey maps.',
        detailed:
          'Synthesizing user interview notes for mobile usability, documenting research findings for dashboard features, updating user personas, creating user journey maps, and sharing research summary with stakeholders.',
      },
    },
    {
      name: 'Competitor UX Analysis',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Competitor Analysis - UX Patterns' },
        { tool: 'notion', title: 'UX Competitive Analysis - Q3 2024' },
        { url: 'https://uxarchive.com', title: 'UX Archive - Mobile patterns' },
        { url: 'https://mobbin.com', title: 'Mobbin - Competitor UI flows' },
      ],
      aiNotes: {
        concise: 'Analyzing competitor user experience patterns.',
        detailed:
          'Conducting competitive analysis of UX patterns in Figma, documenting findings, and researching competitor UI flows and mobile patterns for benchmarking.',
      },
    },
    {
      name: 'Accessibility Testing',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Accessibility Audit - Current Design System' },
        { tool: 'figma', title: 'Color Contrast Improvements' },
        { tool: 'notion', title: 'Accessibility Compliance Documentation' },
        { url: 'https://webaim.org', title: 'WebAIM - Contrast checker' },
        { url: 'https://a11yproject.com', title: 'A11y Project - Guidelines' },
      ],
      aiNotes: {
        concise: 'Testing and improving design system accessibility.',
        detailed:
          'Conducting accessibility audit of design system components, implementing color contrast improvements, documenting accessibility compliance, and researching best practices for accessible design.',
      },
    },
    {
      name: 'User Interview Preparation',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'User Interview Script - Mobile Navigation' },
        { tool: 'figma', title: 'User Testing Prototypes - Navigation Variations' },
        { tool: 'slack', title: 'user-research-team' },
        { url: 'https://nngroup.com', title: 'Nielsen Norman Group - Interview techniques' },
      ],
      aiNotes: {
        concise: 'Preparing user interview scripts and test materials.',
        detailed:
          'Developing user interview script for mobile navigation testing, creating interactive prototypes for testing different navigation variations, and researching effective interview techniques.',
      },
    },
    {
      name: 'Design System Documentation',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Design System - Component Documentation' },
        { tool: 'figma', title: 'Design System - Usage Guidelines' },
        { tool: 'notion', title: 'Design System - Implementation Guide for Devs' },
        { tool: 'linear', title: 'TT-4087: Update design system documentation' },
      ],
      aiNotes: {
        concise: 'Creating comprehensive documentation for design system.',
        detailed:
          'Developing detailed documentation for design system components and usage guidelines in Figma, creating implementation guides for developers, and tracking documentation tasks in Linear.',
      },
    },
    {
      name: 'Interaction Design',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Interactive Dashboard - Prototype v2' },
        { tool: 'figma', title: 'Microinteractions - Component States' },
        { tool: 'figma', title: 'Interactive Prototype - User Flows' },
        { url: 'https://principleformac.com', title: 'Principle - Animation reference' },
      ],
      aiNotes: {
        concise: 'Creating interactive prototypes and microinteractions.',
        detailed:
          'Building interactive dashboard prototypes, designing component state microinteractions, creating user flow prototypes, and researching animation reference materials.',
      },
    },
    {
      name: 'Illustration Work',
      predominantTool: 'adobe_illustrator',
      entries: [
        { tool: 'adobe_illustrator', title: 'Brand Illustrations - Q3 Campaign' },
        { tool: 'adobe_illustrator', title: 'Feature Icons - Dashboard Update' },
        { tool: 'figma', title: 'Illustration Integration - Marketing Site' },
        { url: 'https://dribbble.com', title: 'Dribbble - Illustration inspiration' },
      ],
      aiNotes: {
        concise: 'Creating brand illustrations and icons for marketing.',
        detailed:
          'Designing brand illustrations for marketing campaign and feature icons for dashboard update in Adobe Illustrator, integrating illustrations into the marketing site, and finding inspiration from Dribbble.',
      },
    },
    {
      name: 'Design Exploration',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Exploration - Dashboard Alternative Layouts' },
        { tool: 'figma', title: 'Exploration - Typography System Refresh' },
        { tool: 'figma', title: 'Exploration - Dark Theme Variations' },
        { url: 'https://pinterest.com', title: 'Pinterest - Dashboard inspiration board' },
      ],
      aiNotes: {
        concise: 'Exploring alternative design directions and concepts.',
        detailed:
          'Creating exploratory designs for dashboard layouts, typography system refresh, and dark theme variations. Gathering inspiration from design resources to inform new directions.',
      },
    },
    {
      name: 'User Flow Mapping',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'User Flow - Onboarding Process v2' },
        { tool: 'figma', title: 'User Flow - Authentication Scenarios' },
        { tool: 'notion', title: 'User Flow Documentation - Onboarding' },
        { url: 'https://flowmapp.com', title: 'FlowMapp - User flow techniques' },
      ],
      aiNotes: {
        concise: 'Mapping user flows for onboarding and authentication.',
        detailed:
          'Creating user flow diagrams for onboarding process and authentication scenarios, documenting flow details, and researching best practices for user flow design.',
      },
    },
    {
      name: 'Email Template Design',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Email Templates - Welcome Series' },
        { tool: 'figma', title: 'Email Templates - Newsletter Refresh' },
        { tool: 'figma', title: 'Email Templates - Transactional Messages' },
        { url: 'https://reallygoodemails.com', title: 'Really Good Emails - Inspiration' },
      ],
      aiNotes: {
        concise: 'Designing email templates for various communication types.',
        detailed:
          'Creating email template designs for welcome series, newsletter refresh, and transactional messages, researching email design inspiration and best practices.',
      },
    },
    {
      name: 'Marketing Design Support',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Marketing Site - Homepage Refresh' },
        { tool: 'figma', title: 'Landing Page - Q3 Campaign' },
        { tool: 'figma', title: 'Social Media Assets - Product Launch' },
        { tool: 'slack', title: 'marketing-design' },
      ],
      aiNotes: {
        concise: 'Creating marketing site designs and campaign assets.',
        detailed:
          'Designing marketing site homepage refresh, landing page for Q3 campaign, and social media assets for product launch. Collaborating with marketing team on design requirements.',
      },
    },
    {
      name: 'Design Workshop Preparation',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Workshop - Design Sprint Materials' },
        { tool: 'notion', title: 'Design Workshop - Facilitation Guide' },
        { tool: 'miro', title: 'Design Sprint - Workshop Templates' },
        { url: 'https://designsprintkit.withgoogle.com', title: 'Google Design Sprint Kit' },
      ],
      aiNotes: {
        concise: 'Preparing materials for design sprint workshop.',
        detailed:
          'Creating design sprint workshop materials in Figma, developing facilitation guide, preparing workshop templates in Miro, and researching design sprint methodologies.',
      },
    },
  ],
};

/**
 * Manager profile with specific client and project details
 */
export const managerProfile: Profile = {
  name: 'Manager',
  minMeetingsPerDay: 2,
  averageSwitchingFrequencySeconds: 240, // Switch context every ~4 minutes on average
  meetings: [
    {
      title: 'Quarterly Business Review',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Client Success Stories - H1 2024.pptx' },
        { tool: 'microsoft_excel', title: 'Client Satisfaction Metrics - June 2024.xlsx' },
        { tool: 'notion', title: 'Acme Corp QBR Notes' },
        { tool: 'slack', title: 'client-acme' },
      ],
      aiNotes: {
        concise: 'Reviewing quarterly business performance with client.',
        detailed:
          'Presenting client success stories and satisfaction metrics from H1 2024, documenting meeting notes for Acme Corp quarterly business review, and communicating with client team through Slack.',
      },
    },
    {
      title: 'TechDynamics - Project Planning',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_excel', title: 'Project Timelines - Q3 2024.xlsx' },
        { tool: 'notion', title: 'TechDynamics Project Requirements' },
        { tool: 'jira', title: 'TD-198: TechDynamics - API Integration Phase 2' },
        { tool: 'slack', title: 'project-techdynamics' },
      ],
      aiNotes: {
        concise: 'Planning project timelines and requirements for client.',
        detailed:
          'Reviewing project timelines for Q3 2024, discussing TechDynamics project requirements, planning API Integration Phase 2, and coordinating with project team through Slack.',
      },
    },
    {
      title: 'GlobalHealth - Implementation Kickoff',
      durationMinutes: 120,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Implementation Process Overview.pptx' },
        { tool: 'notion', title: 'GlobalHealth Implementation Plan' },
        { tool: 'jira', title: 'GH-421: GlobalHealth - User Provisioning System' },
        { tool: 'slack', title: 'project-globalhealth' },
      ],
      aiNotes: {
        concise: 'Kicking off implementation project with new client.',
        detailed:
          'Conducting implementation kickoff meeting for GlobalHealth, presenting overview of implementation process, discussing user provisioning system requirements, and communicating with project team on Slack.',
      },
    },
    {
      title: 'Daily Team Standup',
      durationMinutes: 15,
      entries: [
        { tool: 'notion', title: 'Daily Standup Notes' },
        { tool: 'jira', title: 'Sprint Board' },
        { tool: 'slack', title: 'leadership-team' },
      ],
      aiNotes: {
        concise: 'Daily team status update and coordination meeting.',
        detailed:
          'Facilitating daily standup meeting with leadership team, reviewing sprint board tasks in Jira, taking notes, and discussing team priorities.',
      },
    },
    {
      title: '1:1 with Sarah (Design Lead)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Performance Review Notes: Design Team' },
        { tool: 'microsoft_excel', title: 'Design Team Metrics - Q2 2024.xlsx' },
        { tool: 'slack', title: 'Sarah (DM)' },
      ],
      aiNotes: {
        concise: 'Reviewing design team performance metrics.',
        detailed:
          'Meeting with Design Lead to discuss design team performance metrics, reviewing Q2 data, preparing for upcoming performance reviews, and exchanging feedback through direct messages.',
      },
    },
    {
      title: '1:1 with Michael (Engineering Manager)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Engineering Roadmap Discussion' },
        { tool: 'jira', title: 'Engineering Team Sprint Dashboard' },
        { tool: 'slack', title: 'Michael (DM)' },
      ],
      aiNotes: {
        concise: 'Planning engineering team roadmap and sprint priorities.',
        detailed:
          'One-on-one meeting with Engineering Manager to discuss engineering roadmap, review current sprint dashboard progress, and coordinate on upcoming development priorities through direct messages.',
      },
    },
    {
      title: 'Department All-Hands',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Department Performance - Q2 2024.pptx' },
        { tool: 'notion', title: 'Team OKRs Q2 2024' },
        { tool: 'slack', title: 'all-hands-announcements' },
      ],
      aiNotes: {
        concise: 'Presenting department performance and reviewing quarterly objectives.',
        detailed:
          'Leading department all-hands meeting, presenting Q2 performance results, discussing team OKRs achievement status, and sharing important announcements with the entire department.',
      },
    },
    {
      title: 'Executive Team Meeting',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Executive Updates - Q2 2024.pptx' },
        { tool: 'microsoft_excel', title: 'Revenue Forecast - H2 2024.xlsx' },
        { tool: 'notion', title: 'Executive Team Agenda' },
        { tool: 'slack', title: 'executive-team' },
      ],
      aiNotes: {
        concise: 'Discussing quarterly results and revenue forecasts with executive team.',
        detailed:
          'Participating in executive team meeting, reviewing Q2 performance updates, analyzing H2 revenue forecasts, following the structured agenda, and coordinating with leadership through executive Slack channel.',
      },
    },
    {
      title: 'Board Meeting Prep',
      durationMinutes: 60,
      entries: [
        { tool: 'keynote', title: 'Board Meeting Presentation - July 2024' },
        { tool: 'microsoft_excel', title: 'Financial Summary - Board Report.xlsx' },
        { tool: 'notion', title: 'Board Meeting Agenda' },
      ],
      aiNotes: {
        concise: 'Preparing presentation materials for upcoming board meeting.',
        detailed:
          'Creating board meeting presentation slides, organizing financial summary data for the board report, and finalizing the meeting agenda for the upcoming July board meeting.',
      },
    },
  ],
  sessions: [
    {
      name: 'Project Planning',
      predominantTool: 'jira',
      entries: [
        { tool: 'jira', title: 'ACM-342: Dashboard Implementation' },
        { tool: 'jira', title: 'TD-198: TechDynamics - API Integration Phase 2' },
        { tool: 'jira', title: 'GH-421: GlobalHealth - User Provisioning System' },
        { tool: 'notion', title: 'Project Plan: Acme Corp Q3-Q4 Roadmap' },
        { tool: 'notion', title: 'Resource Allocation: Q3 2024' },
        { tool: 'microsoft_excel', title: 'Project Timelines - Q3 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Resource Allocation Model.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Services Budget 2024.xlsx' },
        { url: 'https://miro.com', title: 'Miro - Strategic planning workshop' },
      ],
      aiNotes: {
        concise: 'Planning client project timelines and resource allocation.',
        detailed:
          'Managing client projects in Jira for Acme Corp, TechDynamics, and GlobalHealth, creating Q3-Q4 roadmaps, allocating resources, developing project timelines, budgeting for client services, and participating in strategic planning workshops.',
      },
    },
    {
      name: 'Report Preparation',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Q2 2024 - Department Performance.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Satisfaction Metrics - June 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Revenue Forecast - H2 2024.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'Executive Presentation - Q2 Results.pptx' },
        { tool: 'microsoft_powerpoint', title: 'Client Success Stories - H1 2024.pptx' },
        { tool: 'microsoft_word', title: 'Department Strategy Document - 2024.docx' },
        { tool: 'keynote', title: 'Board Meeting Presentation - July 2024' },
        { url: 'https://tableau.com', title: 'Tableau - Department performance dashboard' },
        { url: 'https://slideshare.net', title: 'SlideShare - Industry benchmark data' },
      ],
      aiNotes: {
        concise: 'Creating performance reports and presentations for stakeholders.',
        detailed:
          'Analyzing department performance data, client satisfaction metrics, and revenue forecasts. Creating executive presentations showcasing Q2 results, client success stories, and preparing board meeting materials with industry benchmark comparisons.',
      },
    },
    {
      name: 'Communication',
      predominantTool: 'slack',
      entries: [
        { tool: 'slack', title: 'leadership-team' },
        { tool: 'slack', title: 'client-success' },
        { tool: 'slack', title: 'project-acme' },
        { tool: 'slack', title: 'Jennifer (DM)' },
        { tool: 'slack', title: 'David (DM)' },
        { tool: 'gmail', title: 'Email to Acme Corp: Quarterly Review Follow-up' },
        { tool: 'gmail', title: 'Email from GlobalHealth: Implementation Questions' },
        { tool: 'gmail', title: 'Email thread: Executive Team - Budget Approval' },
        { tool: 'gmail', title: 'Email thread: Department All-Hands Agenda' },
      ],
      aiNotes: {
        concise: 'Coordinating with leadership team and clients through various channels.',
        detailed:
          'Communicating through leadership and client-specific Slack channels, direct messaging team members, following up on Acme Corp quarterly review, addressing GlobalHealth implementation questions, and coordinating budget approvals and department meetings.',
      },
    },
    {
      name: 'Strategic Planning',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Strategic Plan: 2024-2025' },
        { tool: 'notion', title: 'Competitive Analysis: Market Positioning' },
        { tool: 'notion', title: 'Product Roadmap: 18-Month Vision' },
        { tool: 'microsoft_excel', title: 'Growth Scenarios - 2024-2026.xlsx' },
        { tool: 'microsoft_excel', title: 'Department Hiring Plan.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'Vision Presentation - Leadership Offsite.pptx' },
        { tool: 'jira', title: 'STRAT-42: Define Q3-Q4 Strategic Initiatives' },
        { url: 'https://hbr.org', title: 'Harvard Business Review - Team productivity article' },
        { url: 'https://gartner.com', title: 'Gartner - Industry trends report 2024' },
      ],
      aiNotes: {
        concise: 'Developing long-term strategy and competitive positioning.',
        detailed:
          'Creating strategic plan documentation, analyzing market positioning against competitors, mapping 18-month product vision, modeling growth scenarios, planning department hiring, preparing vision presentations for leadership offsite, and researching industry trends.',
      },
    },
    {
      name: 'Market Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://linkedin.com', title: 'LinkedIn - Industry updates for client meeting' },
        { url: 'https://wsj.com', title: 'Wall Street Journal - Tech sector outlook' },
        { url: 'https://gartner.com', title: 'Gartner - SaaS market forecast 2024-2026' },
        { url: 'https://google.com', title: 'Google search: Client retention strategies SaaS' },
        { tool: 'notion', title: 'Market Research: Competitive Analysis Q3 2024' },
      ],
      aiNotes: {
        concise: 'Researching industry trends and market forecasts.',
        detailed:
          'Gathering industry updates from LinkedIn for client meetings, reviewing tech sector outlook in Wall Street Journal, analyzing SaaS market forecasts, researching client retention strategies, and documenting competitive analysis findings.',
      },
    },
    {
      name: 'Client Relationship Management',
      predominantTool: 'salesforce',
      entries: [
        { url: 'https://salesforce.com', title: 'Salesforce - Client opportunity pipeline' },
        { url: 'https://salesforce.com', title: 'Salesforce - Customer satisfaction metrics' },
        { url: 'https://salesforce.com', title: 'Salesforce - Account renewal forecasting' },
        { url: 'https://hubspot.com', title: 'HubSpot - Customer engagement analytics' },
        { tool: 'microsoft_excel', title: 'Client Success Analysis - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Retention Analysis - June 2024.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'Client Success Stories - Executive Review.pptx' },
        { tool: 'notion', title: 'Client Success Framework: 2024 Strategy' },
        { tool: 'notion', title: 'Key Account Management Playbook' },
        { tool: 'slack', title: 'client-success' },
        { tool: 'gmail', title: 'Email: Client Relationship Quarterly Review' },
      ],
      aiNotes: {
        concise: 'Managing client relationships and analyzing satisfaction metrics.',
        detailed:
          'Tracking client opportunity pipeline in Salesforce, monitoring customer satisfaction metrics, forecasting account renewals, reviewing engagement analytics, analyzing client success and retention metrics, and documenting client success frameworks and account management strategies.',
      },
    },
    {
      name: 'Performance Reviews',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Performance Review Template - Q2 2024.docx' },
        { tool: 'microsoft_excel', title: 'Team Performance Metrics - Q2 2024.xlsx' },
        { tool: 'notion', title: 'Performance Review Guidelines - Managers' },
        { tool: 'slack', title: 'hr-performance-reviews' },
      ],
      aiNotes: {
        concise: 'Preparing team performance evaluations for quarterly reviews.',
        detailed:
          'Working with performance review templates, analyzing team performance metrics for Q2, reviewing manager guidelines for conducting evaluations, and coordinating with HR team through dedicated Slack channel.',
      },
    },
    {
      name: 'Budget Planning',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Q3 2024 - Department Budget Planning.xlsx' },
        { tool: 'microsoft_excel', title: 'Budget Forecast - 2024-2025.xlsx' },
        { tool: 'microsoft_excel', title: 'Expense Allocation - Q3 2024.xlsx' },
        { tool: 'notion', title: 'Budget Planning Process - 2024' },
      ],
      aiNotes: {
        concise: 'Planning department budget allocations and forecasts.',
        detailed:
          'Developing Q3 department budget plans, creating annual budget forecasts for 2024-2025, allocating expenses for the upcoming quarter, and documenting budget planning processes and guidelines.',
      },
    },
    {
      name: 'Team Hiring',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Job Description - Senior Developer' },
        { tool: 'notion', title: 'Hiring Process - Technical Roles' },
        { tool: 'gmail', title: 'Candidate Resume Review Thread' },
        { tool: 'microsoft_excel', title: 'Candidate Evaluation Matrix.xlsx' },
      ],
      aiNotes: {
        concise: 'Managing technical hiring process and candidate evaluations.',
        detailed:
          'Creating job descriptions for senior developer position, documenting technical role hiring processes, reviewing candidate resumes via email thread, and tracking candidate evaluations using structured evaluation matrix.',
      },
    },
    {
      name: 'Process Improvement Planning',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Process Improvement Initiative - Q3 2024' },
        { tool: 'notion', title: 'Team Workflow Optimization Plan' },
        { tool: 'miro', title: 'Process Flow Mapping Workshop' },
        { tool: 'linear', title: 'OKR-12: Improve team delivery efficiency' },
      ],
      aiNotes: {
        concise: 'Planning workflow optimizations to improve team efficiency.',
        detailed:
          'Developing process improvement initiatives for Q3, planning team workflow optimizations, facilitating process flow mapping workshops, and tracking team delivery efficiency objectives and key results.',
      },
    },
    {
      name: 'Client Proposal Development',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Client Proposal - MegaCorp Expansion.docx' },
        { tool: 'microsoft_excel', title: 'Project Cost Estimation - MegaCorp.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'MegaCorp Proposal - Presentation Deck.pptx' },
        { tool: 'notion', title: 'MegaCorp Client Requirements' },
      ],
      aiNotes: {
        concise: 'Creating expansion proposal and cost estimates for major client.',
        detailed:
          'Drafting client proposal document for MegaCorp expansion project, estimating project costs, developing presentation slides for proposal meeting, and documenting detailed client requirements.',
      },
    },
    {
      name: 'OKR Planning',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Q3-Q4 OKRs - Department Planning' },
        { tool: 'notion', title: 'OKR Alignment - Cross-Functional Teams' },
        { tool: 'miro', title: 'OKR Planning Workshop Board' },
        { tool: 'microsoft_excel', title: 'OKR Tracking Template - 2024.xlsx' },
      ],
      aiNotes: {
        concise: 'Establishing objectives and key results for upcoming quarters.',
        detailed:
          'Planning department OKRs for Q3-Q4, aligning objectives across cross-functional teams, facilitating collaborative OKR planning workshops, and developing tracking templates for measuring progress.',
      },
    },
    {
      name: 'Vendor Management',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Vendor Comparison Matrix - 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Vendor Agreement - Software Solutions Inc.docx' },
        { tool: 'microsoft_excel', title: 'Vendor Performance Tracking.xlsx' },
        { tool: 'gmail', title: 'Vendor Negotiation Thread - Software Solutions' },
      ],
      aiNotes: {
        concise: 'Evaluating vendor options and negotiating service agreements.',
        detailed:
          'Comparing potential vendors using evaluation matrices, reviewing vendor agreement terms with Software Solutions Inc., tracking vendor performance metrics, and conducting contract negotiations via email.',
      },
    },
    {
      name: 'Remote Work Policy Development',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Remote Work Policy - 2024 Update.docx' },
        { tool: 'notion', title: 'Remote Work Guidelines for Managers' },
        { tool: 'microsoft_powerpoint', title: 'Remote Work Policy - Team Presentation.pptx' },
        { tool: 'slack', title: 'policy-development' },
      ],
      aiNotes: {
        concise: 'Updating remote work policies and management guidelines.',
        detailed:
          'Revising remote work policy documentation for 2024, creating guidelines for managers supervising remote teams, preparing presentation slides for policy rollout, and discussing policy updates with development team via Slack.',
      },
    },
    {
      name: 'Risk Assessment',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Project Risk Assessment - Q3 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Risk Mitigation Strategies.docx' },
        { tool: 'notion', title: 'Risk Management Framework - 2024' },
        { tool: 'slack', title: 'risk-compliance' },
      ],
      aiNotes: {
        concise: 'Identifying project risks and developing mitigation strategies.',
        detailed:
          'Conducting project risk assessments for Q3, documenting risk mitigation strategies, updating the risk management framework, and coordinating with compliance team through dedicated Slack channel.',
      },
    },
  ],
};

/**
 * Office worker profile with specific project details
 */
export const officeProfile: Profile = {
  name: 'Office',
  minMeetingsPerDay: 2,
  averageSwitchingFrequencySeconds: 360, // Switch context every ~6 minutes on average
  meetings: [
    {
      title: 'Westfield Industries - Status Update',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_excel', title: 'Project Timeline - Westfield Industries.xlsx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Westfield Implementation.docx' },
        { tool: 'microsoft_outlook', title: 'RE: Westfield Project Status' },
        { tool: 'sharepoint', title: 'Client Documentation - Westfield Industries' },
      ],
      aiNotes: {
        concise: 'Reviewing implementation progress with client project team.',
        detailed:
          'Meeting with Westfield Industries team to discuss project timeline, document implementation progress, reference previous email communications, and update client documentation in SharePoint.',
      },
    },
    {
      title: 'Department All-Hands Meeting',
      durationMinutes: 45,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Department Performance - Q2 2024.pptx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Department Meeting.docx' },
        { tool: 'sharepoint', title: 'Department Plans and Policies' },
      ],
      aiNotes: {
        concise: 'Attending department-wide meeting on quarterly performance.',
        detailed:
          'Participating in department all-hands meeting, viewing quarterly performance presentation, taking detailed meeting notes, and referencing department plans and policies documentation.',
      },
    },
    {
      title: 'Project Kickoff: Atlas Group',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Onboarding Presentation - Atlas Group.pptx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Atlas Group Onboarding.docx' },
        { tool: 'microsoft_excel', title: 'Project Timeline - Atlas Group.xlsx' },
        { tool: 'sharepoint', title: 'Client Documentation - Atlas Group' },
      ],
      aiNotes: {
        concise: 'Launching new client implementation project with timeline planning.',
        detailed:
          'Facilitating project kickoff meeting for Atlas Group, presenting onboarding information, documenting detailed meeting notes, reviewing project timeline, and organizing client documentation in SharePoint.',
      },
    },
    {
      title: 'Budget Review Meeting',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_excel', title: 'Department Budget - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Budget Variance Analysis.xlsx' },
        { tool: 'microsoft_word', title: 'Budget Variance Report - June 2024.docx' },
      ],
      aiNotes: {
        concise: 'Analyzing quarterly budget performance and variances.',
        detailed:
          'Reviewing department budget for Q2 2024, analyzing budget variances, examining financial performance data, and preparing formal variance reports for June 2024.',
      },
    },
    {
      title: '1:1 with Mark',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_word', title: 'Project Status Updates.docx' },
        { tool: 'microsoft_excel', title: 'Resource Allocation - Q3 2024.xlsx' },
        { tool: 'microsoft_outlook', title: 'Calendar' },
      ],
      aiNotes: {
        concise: 'One-on-one meeting discussing project status and resource planning.',
        detailed:
          'Meeting with Mark to discuss project status updates, planning Q3 resource allocation, and coordinating calendar schedules for upcoming project activities.',
      },
    },
    {
      title: 'HR Meeting: Quarterly Reviews',
      durationMinutes: 45,
      entries: [
        { tool: 'microsoft_word', title: 'Performance Review Template - 2024.docx' },
        { tool: 'microsoft_excel', title: 'Team Performance Metrics - Q2.xlsx' },
        { tool: 'sharepoint', title: 'Human Resources - Policies and Procedures' },
      ],
      aiNotes: {
        concise: 'Preparing for upcoming team performance evaluation process.',
        detailed:
          'Meeting with HR to discuss quarterly performance review process, examining performance review templates, reviewing team metrics from Q2, and referencing HR policies and procedures for the evaluation process.',
      },
    },
    {
      title: 'Client Call: Carter Healthcare',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_word', title: 'Requirements Document - Carter Healthcare.docx' },
        { tool: 'microsoft_excel', title: 'Client Requirements Matrix - Q2 2024.xlsx' },
        { tool: 'microsoft_outlook', title: 'FW: Carter Healthcare Follow-up' },
      ],
      aiNotes: {
        concise: 'Discussing implementation requirements with healthcare client.',
        detailed:
          'Conducting call with Carter Healthcare to review requirements documentation, updating client requirements matrix, and following up on previous email communications about implementation details.',
      },
    },
  ],
  sessions: [
    {
      name: 'Client Documentation',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Client Proposal - Westfield Industries.docx' },
        { tool: 'microsoft_word', title: 'Service Agreement - Pinnacle Solutions.docx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Atlas Group Onboarding.docx' },
        { tool: 'microsoft_word', title: 'Requirements Document - Carter Healthcare.docx' },
        { tool: 'microsoft_excel', title: 'Client Requirements Matrix - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Project Timeline - Westfield Industries.xlsx' },
        { tool: 'sharepoint', title: 'Client Documentation - Westfield Industries' },
        { url: 'https://sharepoint.com', title: 'SharePoint - Document repository access' },
        { url: 'https://salesforce.com', title: 'Salesforce - Customer account information' },
        { url: 'https://hoovers.com', title: 'Hoovers - Client company research' },
      ],
      aiNotes: {
        concise: 'Preparing client proposals, agreements, and project documentation.',
        detailed:
          'Creating and updating client documentation including proposals for Westfield Industries, service agreements for Pinnacle Solutions, meeting notes for Atlas Group, and requirements documents for Carter Healthcare. Organizing client information in SharePoint and retrieving account details from Salesforce.',
      },
    },
    {
      name: 'Financial Reporting',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Department Budget - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Expense Report - June 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Revenue Forecast - H2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Project Profitability Analysis.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Invoicing Status - June 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Budget Variance Report - June 2024.docx' },
        { tool: 'sharepoint', title: 'Finance Department - Monthly Reports' },
        { url: 'https://youtube.com', title: 'YouTube - Microsoft Excel: Advanced PivotTables' },
        { url: 'https://google.com', title: 'Google search: Excel VLOOKUP with multiple criteria' },
        { url: 'https://concur.com', title: 'Concur - Expense report submission' },
      ],
      aiNotes: {
        concise: 'Creating financial reports and analyzing budget data.',
        detailed:
          'Preparing quarterly budget reports, tracking monthly expenses, forecasting revenue for H2 2024, analyzing project profitability, monitoring client invoicing status, and drafting budget variance reports. Researching advanced Excel techniques for improved financial analysis.',
      },
    },
    {
      name: 'General Communication',
      predominantTool: 'microsoft_teams',
      entries: [
        { tool: 'microsoft_teams', title: 'Chat | Mark | Microsoft Teams' },
        { tool: 'microsoft_teams', title: 'Chat | Susan | Microsoft Teams' },
        { tool: 'microsoft_outlook', title: 'Mail: Atlas Group Implementation Timeline' },
        { tool: 'microsoft_outlook', title: 'Mail: Budget Approval for Carter Healthcare' },
        { tool: 'microsoft_outlook', title: 'Mail: Weekly Status Report Distribution' },
        { url: 'https://google.com', title: 'Google search: Professional email templates' },
      ],
      aiNotes: {
        concise: 'Coordinating with team members and clients via messaging and email.',
        detailed:
          'Communicating with colleagues through Microsoft Teams chats, sending and responding to emails about Atlas Group implementation timeline, processing budget approvals for Carter Healthcare, distributing weekly status reports, and researching professional email templates.',
      },
    },
    {
      name: 'Presentation Preparation',
      predominantTool: 'microsoft_powerpoint',
      entries: [
        {
          tool: 'microsoft_powerpoint',
          title: 'Client Quarterly Review - Westfield Industries.pptx',
        },
        {
          tool: 'microsoft_powerpoint',
          title: 'New Service Offering - Executive Presentation.pptx',
        },
        { tool: 'microsoft_powerpoint', title: 'Department Performance - Q2 2024.pptx' },
        { tool: 'microsoft_powerpoint', title: 'Onboarding Presentation - Atlas Group.pptx' },
        { tool: 'microsoft_excel', title: 'Presentation Data - Client Growth Metrics.xlsx' },
        { tool: 'microsoft_word', title: 'Presentation Speaking Notes - Executive Review.docx' },
        { url: 'https://google.com', title: 'Google search: PowerPoint animations best practices' },
      ],
      aiNotes: {
        concise: 'Creating presentations for client reviews and executive meetings.',
        detailed:
          'Developing PowerPoint presentations for Westfield Industries quarterly review, new service offering executive presentation, department performance report, and Atlas Group onboarding. Compiling supporting data on client growth metrics and preparing speaker notes for executive review.',
      },
    },
    {
      name: 'HR Administration',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Job Description - Senior Account Manager.docx' },
        { tool: 'microsoft_word', title: 'Performance Review Template - 2024.docx' },
        { tool: 'microsoft_excel', title: 'Team Vacation Schedule - Q3 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Training Completion Tracking.xlsx' },
        { tool: 'microsoft_teams', title: 'Chat | HR Team | Microsoft Teams' },
        { tool: 'sharepoint', title: 'Human Resources - Policies and Procedures' },
        { tool: 'jira', title: 'HR-127: Update employee handbook for remote work policy' },
        { url: 'https://indeed.com', title: 'Indeed - Senior Account Manager job descriptions' },
        { url: 'https://linkedin.com', title: 'LinkedIn - Industry networking' },
      ],
      aiNotes: {
        concise: 'Managing HR documentation and personnel processes.',
        detailed:
          'Creating job descriptions for senior account manager position, preparing performance review templates, maintaining team vacation schedules, tracking employee training completion, coordinating with HR team, updating employee handbook for remote work policy, and researching industry job descriptions.',
      },
    },
    {
      name: 'Administrative Tasks',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Travel Expense Report - Boston Conference.xlsx' },
        { tool: 'microsoft_excel', title: 'Office Supply Inventory.xlsx' },
        { tool: 'microsoft_excel', title: 'Meeting Room Scheduling - July 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Department Process Documentation.docx' },
        { tool: 'microsoft_teams', title: 'Chat | Admin Team | Microsoft Teams' },
        { tool: 'microsoft_outlook', title: 'Calendar: July Planning Meetings' },
        { tool: 'sharepoint', title: 'Administrative Templates and Forms' },
        { url: 'https://office.com', title: 'Microsoft 365 - Admin Portal' },
      ],
      aiNotes: {
        concise: 'Managing office operations and administrative processes.',
        detailed:
          'Processing travel expense reports for Boston conference, maintaining office supply inventory, coordinating meeting room schedules, updating department process documentation, communicating with administrative team, scheduling July planning meetings, and accessing administrative templates and forms.',
      },
    },
    {
      name: 'Client Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://hoovers.com', title: 'Hoovers - Client company research' },
        { url: 'https://linkedin.com', title: 'LinkedIn - Client company profiles' },
        { url: 'https://linkedin.com', title: 'LinkedIn - Potential client stakeholders' },
        { url: 'https://salesforce.com', title: 'Salesforce - Customer history review' },
        { url: 'https://zoominfo.com', title: 'ZoomInfo - Company contact information' },
        { url: 'https://dnb.com', title: 'Dun & Bradstreet - Company financial data' },
        { url: 'https://bloomberg.com', title: 'Bloomberg - Industry news and trends' },
        { tool: 'microsoft_word', title: 'Client Research Notes - Potential Opportunities.docx' },
        { tool: 'microsoft_word', title: 'Client Research - Competitor Analysis.docx' },
        { tool: 'microsoft_excel', title: 'Client Comparison Analysis.xlsx' },
        {
          tool: 'microsoft_powerpoint',
          title: 'Client Research Findings - Sales Team Presentation.pptx',
        },
        { tool: 'microsoft_outlook', title: 'RE: Client Research Summary' },
      ],
      aiNotes: {
        concise: 'Gathering intelligence on client companies and stakeholders.',
        detailed:
          'Researching client companies through industry databases, reviewing company profiles, identifying key stakeholders, examining customer history, collecting contact information, analyzing financial data, monitoring industry news, documenting potential opportunities, conducting competitor analysis, and preparing presentation of research findings.',
      },
    },
    {
      name: 'Contract Administration',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Client Contract - Renewal Draft.docx' },
        { tool: 'microsoft_word', title: 'Vendor Agreement - Office Supplies.docx' },
        { tool: 'microsoft_excel', title: 'Contract Renewal Tracking - 2024.xlsx' },
        { tool: 'sharepoint', title: 'Legal Templates - Contracts' },
      ],
      aiNotes: {
        concise: 'Managing client contracts and vendor agreements.',
        detailed:
          'Drafting client contract renewals, preparing vendor agreements for office supplies, tracking contract renewal dates through 2024, and utilizing legal contract templates from the company repository.',
      },
    },
    {
      name: 'Event Planning',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Company Retreat - Planning Sheet.xlsx' },
        { tool: 'microsoft_word', title: 'Event Checklist - Client Appreciation Day.docx' },
        { tool: 'microsoft_outlook', title: 'Event Invitation - Client Appreciation' },
        { tool: 'sharepoint', title: 'Event Planning Resources' },
      ],
      aiNotes: {
        concise: 'Organizing company retreat and client appreciation events.',
        detailed:
          'Creating detailed planning sheets for company retreat, developing event checklists for Client Appreciation Day, drafting and sending event invitations, and utilizing event planning templates and resources.',
      },
    },
    {
      name: 'Office Management',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Office Inventory - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Facilities Maintenance Schedule.xlsx' },
        { tool: 'microsoft_word', title: 'Office Policies - 2024 Update.docx' },
        { tool: 'microsoft_teams', title: 'Chat | Facilities Management' },
      ],
      aiNotes: {
        concise: 'Managing office facilities, supplies, and operational policies.',
        detailed:
          'Maintaining office inventory records for Q2, scheduling facility maintenance activities, updating office policies documentation for 2024, and coordinating with facilities management team on operational matters.',
      },
    },
    {
      name: 'Travel Coordination',
      predominantTool: 'microsoft_outlook',
      entries: [
        { tool: 'microsoft_outlook', title: 'Travel Arrangements - Executive Team' },
        { tool: 'microsoft_excel', title: 'Travel Expense Tracking - Q2 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Travel Policy - International Guidelines.docx' },
        { url: 'https://concur.com', title: 'Concur - Travel booking portal' },
      ],
      aiNotes: {
        concise: 'Arranging business travel and processing expense reports.',
        detailed:
          "Coordinating travel arrangements for executive team members, tracking travel expenses for Q2, reviewing international travel policy guidelines, and booking travel through the company's expense management portal.",
      },
    },
    {
      name: 'Meeting Coordination',
      predominantTool: 'microsoft_outlook',
      entries: [
        { tool: 'microsoft_outlook', title: 'Calendar - Board Meeting Scheduling' },
        { tool: 'microsoft_word', title: 'Meeting Agenda Template - Client Reviews.docx' },
        { tool: 'microsoft_excel', title: 'Conference Room Scheduling - July 2024.xlsx' },
        { tool: 'microsoft_teams', title: 'Chat | Meeting Coordination' },
      ],
      aiNotes: {
        concise: 'Scheduling important meetings and preparing meeting materials.',
        detailed:
          'Coordinating board meeting schedules through calendar management, creating agenda templates for client review meetings, managing conference room bookings for July, and communicating with meeting coordination team through Teams.',
      },
    },
    {
      name: 'Invoice Processing',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Invoice Tracking - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Accounts Payable Status - June 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Invoice Approval Process.docx' },
        { tool: 'sharepoint', title: 'Finance - Invoice Templates' },
      ],
      aiNotes: {
        concise: 'Managing client invoices and accounts payable processes.',
        detailed:
          'Tracking invoices for Q2, monitoring accounts payable status for June, documenting invoice approval processes, and utilizing finance department invoice templates for standardized billing.',
      },
    },
    {
      name: 'Training Coordination',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Training Materials - New Software Rollout.docx' },
        { tool: 'microsoft_excel', title: 'Training Attendance Tracking - Q2 2024.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'New Hire Orientation - July 2024.pptx' },
        { tool: 'microsoft_teams', title: 'Chat | Training Department' },
      ],
      aiNotes: {
        concise: 'Organizing employee training programs and materials.',
        detailed:
          'Preparing training materials for new software rollout, tracking employee training attendance for Q2, developing presentation for July new hire orientation, and coordinating with training department on implementation plans.',
      },
    },
    {
      name: 'Policy Documentation',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Employee Handbook - 2024 Update.docx' },
        { tool: 'microsoft_word', title: 'Information Security Policy.docx' },
        { tool: 'microsoft_word', title: 'Remote Work Guidelines - 2024.docx' },
        { tool: 'sharepoint', title: 'Human Resources - Policy Library' },
      ],
      aiNotes: {
        concise: 'Updating company policies and employee guidelines.',
        detailed:
          'Revising the employee handbook for 2024, documenting information security policies, updating remote work guidelines, and organizing policy documentation in the HR policy library.',
      },
    },
    {
      name: 'Benefits Administration',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Benefits Enrollment Tracking - 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Benefits Summary - Employee Guide.docx' },
        { tool: 'microsoft_powerpoint', title: 'Benefits Overview - New Hires.pptx' },
        { tool: 'microsoft_outlook', title: 'Benefits Enrollment Reminder' },
      ],
      aiNotes: {
        concise: 'Managing employee benefits programs and enrollment.',
        detailed:
          'Tracking employee benefits enrollment for 2024, creating comprehensive benefits summary guide for employees, developing presentations on benefits options for new hires, and sending enrollment deadline reminders.',
      },
    },
    {
      name: 'Data Entry and Management',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Customer Database - June 2024 Updates.xlsx' },
        { tool: 'microsoft_excel', title: 'Sales Data Compilation - Q2 2024.xlsx' },
        { tool: 'microsoft_access', title: 'Department Records Database' },
        { tool: 'microsoft_teams', title: 'Chat | Data Management' },
      ],
      aiNotes: {
        concise: 'Maintaining customer databases and compiling sales records.',
        detailed:
          'Updating customer database records for June 2024, compiling sales data for Q2 reports, managing department records in Access database, and coordinating with data management team on data integrity and maintenance processes.',
      },
    },
  ],
};

export const profiles: Record<DemoMemoryPreset, Profile> = {
  developer: developerProfile,
  designer: designerProfile,
  manager: managerProfile,
  office: officeProfile,
  seeing_machines: seeingMachinesProfile,
};
